/** The pull plan's undo history: a compact Undo control that expands into the
 *  last ~30 actions. Every board/deps action snapshots the full draft state
 *  BEFORE it runs; Undo (or ⌘/Ctrl+Z) steps back one, clicking an entry jumps
 *  back to just before that action. */
import React from "react";
import "./HistoryPanel.css";

export interface HistoryItem {
  id: number;
  label: string;
  at: number;
}

interface Props {
  items: HistoryItem[]; // newest first
  onUndo: () => void;
  onJumpTo: (id: number) => void;
}

function ago(ts: number): string {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

const HistoryPanel: React.FC<Props> = ({ items, onUndo, onJumpTo }) => {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="history" ref={rootRef}>
      <button
        type="button"
        className="history-undo"
        disabled={items.length === 0}
        title="Undo the last action (⌘Z / Ctrl+Z)"
        onClick={onUndo}
      >
        ↵ Undo
      </button>
      {items.length > 0 && (
        <button
          type="button"
          className="history-expand"
          title={`Action history (${items.length})`}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="history-caret">{open ? "▴" : "▾"}</span>
        </button>
      )}
      {open && items.length > 0 && (
        <div className="history-pop">
          <div className="history-pop-title">
            History <span className="history-pop-hint">click to revert to before it</span>
          </div>
          {items.slice(0, 30).map((it) => (
            <button
              key={it.id}
              type="button"
              className="history-item"
              onClick={() => {
                onJumpTo(it.id);
                setOpen(false);
              }}
            >
              <span className="history-item-label">{it.label}</span>
              <span className="history-item-when">{ago(it.at)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default HistoryPanel;
